import React, { useEffect, useRef, useState, useCallback } from 'react'
import '../VideoPlayer/video-player.scss'
import type {
  BrightcovePlayer,
  PlaybackState,
  RawVideoData,
  VideoContainerElement,
  VideoData,
  VideoPlayerProps
} from '../VideoPlayer/VideoPlayer.types'
import { DEFAULT_PLAYER_ID } from '../../lib/constants'
import { generateUniqueId } from '../../lib/generateUniqueId'
import {
  fetchBrightcoveData,
  getVideoResourceUrl
} from '../../lib/fetchBrightcoveData'
import {
  loadBrightcoveScript,
  isScriptAlreadyLoaded
} from '../../lib/loadBrightcoveScript'
import { VideoPoster } from '../VideoPoster/VideoPoster'
import { VideoDescription } from '../VideoDescription/VideoDescription'
import { useVideoPlayerContext } from '../VideoPlayer/VideoPlayerProvider'

export const VideoPlayerDefault: React.FC<VideoPlayerProps> = ({
  accountId,
  policyKey,
  playerId = DEFAULT_PLAYER_ID,
  testId,
  autoplay = false,
  inline = false,
  playlistId,
  className,
  onReady,
  videoId: videoIdProp,
  deferLoad = true
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const videoContainerRef = useRef<HTMLDivElement>(null)
  const playerRef = useRef<BrightcovePlayer | null>(null)
  const [videoData, setVideoData] = useState<VideoData[]>([])
  const videosMapRef = useRef<Record<string, VideoData>>({})
  const initializedRef = useRef(false)
  const brightcoveInitializedRef = useRef(false)

  const [isActive, setIsActive] = useState(!deferLoad)
  const [playbackState, setPlaybackState] = useState<PlaybackState>('idle')
  const [showPosterOverlay, setShowPosterOverlay] = useState(deferLoad)
  const [isOverlayLoading, setIsOverlayLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [fetchResolved, setFetchResolved] = useState(false)
  const [isInsidePromoVideo, setIsInsidePromoVideo] = useState(false)

  const autoPauseContext = useVideoPlayerContext()
  const instanceIdRef = useRef<string>(generateUniqueId())

  useEffect(() => {
    if (initializedRef.current) return
    initializedRef.current = true

    fetchBrightcoveData<RawVideoData & { videos?: RawVideoData[] }>(
      accountId,
      policyKey,
      getVideoResourceUrl(videoIdProp, playlistId)
    )
      .then(playlist => {
        const newVideos: VideoData[] = []
        const existingIds = new Set(
          Object.values(videosMapRef.current).map(v => v.id)
        )

        const processVideo = (video: RawVideoData, idx?: number) => {
          if (existingIds.has(video.id)) return
          const uniquePlayerId = generateUniqueId()
          const processed: VideoData = {
            ...video,
            uniquePlayerId,
            autoplay: autoplay && idx === 0
          }
          videosMapRef.current[uniquePlayerId] = processed
          newVideos.push(processed)
        }

        if (Array.isArray(playlist.videos)) {
          playlist.videos.forEach((video, idx) => processVideo(video, idx))
        } else {
          processVideo(playlist)
        }

        setVideoData(newVideos)
        setFetchResolved(true)
      })
      .catch(() => {
        setPlaybackState('error')
        setErrorMessage('Video unavailable')
        setShowPosterOverlay(true)
        setFetchResolved(true)
      })
  }, [videoIdProp, playlistId, autoplay, accountId, policyKey])

  useEffect(() => {
    if (!isActive) return
    if (videoData.length === 0) return
    if (brightcoveInitializedRef.current) return

    const container = containerRef.current
    if (!container) return

    brightcoveInitializedRef.current = true
    setPlaybackState('loading')

    Object.keys(videosMapRef.current).forEach(key => {
      const videoText = container.querySelector(`#${key}-text-target`)
      if (videoText) {
        videoText.addEventListener('click', () => {
          const videoContainer = container.querySelector(
            `#${key}-vc-video-container`
          )
          const vid = videoContainer?.querySelector('video')
          if (vid && videoContainer?.classList.contains('currently-playing')) {
            vid.pause()
          } else if (vid) {
            vid.play()
          }
        })
      }
    })

    // Track whether the script was already loaded before this instance
    const scriptWasAlreadyLoaded = isScriptAlreadyLoaded()

    loadBrightcoveScript(accountId, playerId)
      .then(() => {
        const vcContainer = videoContainerRef.current
        if (!vcContainer) return

        const videoContainers = vcContainer.querySelectorAll(
          '.vc-video-container'
        )
        const promises: Promise<[BrightcovePlayer, Element, Element]>[] = []

        videoContainers.forEach(vc => {
          const videoEl = vc.querySelector('.video-js')
          if (!videoEl) return
          try {
            // Only call bc() for elements added after the script loaded
            if (scriptWasAlreadyLoaded && window.bc) {
              try {
                window.bc(videoEl)
              } catch {
                /* already initialized */
              }
            }
            promises.push(
              new Promise(resolve =>
                window.videojs(videoEl).ready(function (
                  this: BrightcovePlayer
                ) {
                  resolve([this, vc, videoEl])
                })
              )
            )
          } catch {
            setPlaybackState('error')
            setErrorMessage('Video unavailable')
            setShowPosterOverlay(true)
            setIsOverlayLoading(false)
          }
        })

        Promise.all(promises).then(players => {
          players.forEach(([player, vc, videoEl]) => {
            const vcEl = vc as VideoContainerElement
            vcEl.videoEl = videoEl
            vcEl.player = player

            playerRef.current = player

            player.ready(function (this: BrightcovePlayer) {
              try {
                const playPromise = (
                  this as unknown as { play: () => Promise<void> | void }
                ).play()
                if (playPromise && typeof playPromise.catch === 'function') {
                  playPromise.catch(() => {
                    setShowPosterOverlay(false)
                    setIsOverlayLoading(false)
                    setPlaybackState('paused')
                  })
                }
              } catch {
                setShowPosterOverlay(false)
                setIsOverlayLoading(false)
                setPlaybackState('paused')
              }
            })

            player.on('play', () => {
              autoPauseContext?.notifyPlay(instanceIdRef.current)
              setShowPosterOverlay(false)
              setIsOverlayLoading(false)
              setPlaybackState('playing')
              handlePlayVideo(player, vc as HTMLElement, videoEl as HTMLElement)
            })
            player.on('playing', () => {
              setShowPosterOverlay(false)
              setIsOverlayLoading(false)
              setPlaybackState('playing')

              const firstControl = (vc as HTMLElement).querySelector(
                '.vjs-control-bar button'
              ) as HTMLElement | null

              firstControl?.focus()
            })
            player.on('ended', () =>
              handleEndVideo(player, vc as HTMLElement, videoEl as HTMLElement)
            )
            player.on('pause', () =>
              handlePauseVideo(player, vc as HTMLElement)
            )
            player.on('error', () => {
              setPlaybackState('error')
              setErrorMessage('Video unavailable')
              setShowPosterOverlay(true)
              setIsOverlayLoading(false)
            })
            player.on('loadedmetadata', () => onReady?.())
          })
        })
      })
      .catch(() => {
        setPlaybackState('error')
        setErrorMessage('Failed to load video player')
        setShowPosterOverlay(true)
        setIsOverlayLoading(false)
      })

    return () => {
      const player = playerRef.current
      if (player) {
        try {
          player.dispose()
        } catch {
          // ignore disposal errors on unmount
        }
        playerRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoData, isActive])

  useEffect(() => {
    const el = containerRef.current
    if (el) {
      const promoAncestor = el.closest('.Promo-video, .Promo-video-content')
      const isInsideRedesign = el.closest('.Promo-redesign')
      if (promoAncestor && !isInsideRedesign) {
        setIsInsidePromoVideo(true)
      }
    }
  }, [])

  useEffect(() => {
    if (!autoPauseContext) return
    const id = instanceIdRef.current

    const pauseCallback = () => {
      const player = playerRef.current
      if (player) {
        player.pause()
        setPlaybackState('paused')
      }
    }

    autoPauseContext.register(id, pauseCallback)
    return () => {
      autoPauseContext.unregister(id)
    }
  }, [autoPauseContext])

  const handlePauseVideo = useCallback(
    (player: BrightcovePlayer, container: HTMLElement) => {
      container.classList.remove('currently-playing')
      container.classList.add('currently-paused')
      player.pause()
      setPlaybackState('paused')
      const mediaQuery = window.matchMedia('(max-width: 768px)')
      if (mediaQuery.matches && container.classList.contains('inline')) {
        player.bigPlayButton.el_.style.setProperty('display', 'block')
      }
    },
    []
  )

  const handleEndVideo = useCallback(
    (
      player: BrightcovePlayer,
      container: HTMLElement,
      videoEl: HTMLElement
    ) => {
      player.autoplay(false)
      container.classList.remove('currently-playing')
      container.classList.remove('currently-paused')
      setPlaybackState('ended')
      if (player.isFullscreen()) {
        player.exitFullscreen()
      }
      if (player.isInline) {
        container.classList.add('inline')
        videoEl.classList.add('inline')
      }
      player.posterImage.el_.style.setProperty('display', 'block')
      player.bigPlayButton.el_.style.setProperty('display', 'block')
      player.loadingSpinner.el_.style.setProperty('display', 'none')
      player.currentTime(0)
      player.pause()

      setShowPosterOverlay(true)
      setIsOverlayLoading(false)
    },
    []
  )

  const handlePlayVideo = useCallback(
    (
      player: BrightcovePlayer,
      container: HTMLElement,
      videoEl: HTMLElement
    ) => {
      const vcContainer = videoContainerRef.current
      if (vcContainer) {
        const qualityMenuDivs = vcContainer.querySelectorAll(
          '.vjs-quality-menu-wrapper'
        )
        qualityMenuDivs.forEach(qualityEl => {
          if (qualityEl.classList.contains('vjs-hidden')) {
            qualityEl.classList.remove('vjs-hidden')
            qualityEl.classList.add('vjs-menu-popup-qualityLevel')
          }
        })

        vcContainer.querySelectorAll('.currently-playing').forEach(el => {
          el.classList.remove('currently-playing')
          const vcEl = el as VideoContainerElement
          if (vcEl.player) {
            vcEl.player.pause()
            vcEl.player.bigPlayButton.el_.style.setProperty('display', 'block')
            vcEl.player.posterImage.el_.style.setProperty('display', 'block')
          }
        })
      }

      player.posterImage.el_.style.setProperty('display', 'none')
      player.bigPlayButton.el_.style.setProperty('display', 'none')

      if (videoEl.classList.contains('inline')) {
        player.isInline = true
        videoEl.classList.remove('inline')
        container.classList.remove('inline')
      }

      container.classList.add('currently-playing')
      container.classList.remove('currently-paused')
      setPlaybackState('playing')

      handleControlbarMenus(player.id_, player)
    },
    []
  )

  function handleControlbarMenus(videoId: string, player: BrightcovePlayer) {
    player.loadingSpinner.el_.style.setProperty('display', '')

    const videoElement = videoContainerRef.current?.querySelector(`#${videoId}`)
    if (!videoElement) return

    const controlBar = videoElement.querySelector('.vjs-control-bar')
    if (!controlBar) return

    const children = Array.from(controlBar.children)
    const playbackButton = children.find(el =>
      el.textContent?.includes('Playback Rate')
    )
    const qualityButton = children.find(el =>
      el.textContent?.includes('Quality Levels')
    )
    const playbackMenu = playbackButton?.querySelector('.vjs-menu') ?? null
    const qualityMenu = qualityButton?.querySelector('.vjs-menu') ?? null

    controlBar.addEventListener('mouseenter', () => {
      playbackMenu?.classList.remove('vjs-menu-popup-playback')
      qualityMenu?.classList.remove('vjs-menu-popup-quality')
    })

    playbackButton?.addEventListener('mouseenter', () => {
      playbackMenu?.classList.add('vjs-menu-popup-playback')
      qualityMenu?.classList.remove(
        'vjs-menu-popup-quality',
        'vjs-lock-showing'
      )
    })

    qualityButton?.addEventListener('mouseenter', () => {
      playbackMenu?.classList.remove('vjs-menu-popup-playback')
      qualityMenu?.classList.add('vjs-menu-popup-quality')
      qualityMenu?.classList.remove('vjs-lock-showing')
    })

    qualityButton?.addEventListener('click', () => {
      qualityMenu?.classList.remove('vjs-lock-showing')
    })

    playbackMenu?.addEventListener('mouseleave', () => {
      playbackMenu.classList.remove('vjs-menu-popup-playback')
    })

    qualityMenu?.addEventListener('mouseleave', () => {
      qualityMenu.classList.remove('vjs-menu-popup-quality')
    })
  }

  const handleActivate = () => {
    if (isOverlayLoading) return

    if (playerRef.current) {
      setShowPosterOverlay(false)
      setIsOverlayLoading(false)

      const playPromise = (
        playerRef.current as unknown as {
          play: () => Promise<void> | void
        }
      ).play()

      if (
        playPromise &&
        typeof playPromise === 'object' &&
        'catch' in playPromise
      ) {
        playPromise.catch(() => {
          setPlaybackState('paused')
          setShowPosterOverlay(true)
        })
      }

      return
    }

    setIsOverlayLoading(true)
    setPlaybackState('loading')
    setIsActive(true)
  }

  if (fetchResolved && videoData.length === 0) {
    return null
  }

  return (
    <div
      className={`video-player ${className ?? ''}${isInsidePromoVideo ? ' Promo-video-layout' : ''}`}
      ref={containerRef}
      data-testid={testId}
    >
      <div ref={videoContainerRef}>
        {videoData.map(video => (
          <div
            key={video.uniquePlayerId}
            id={`${video.uniquePlayerId}-vc-video-container`}
            className={`vc-video-container${inline ? ' inline' : ''}`}
          >
            <div className="sixteen-by-nine-container">
              {isActive && (
                <div>
                  <video
                    className={`video-js${inline ? ' inline' : ''}`}
                    controls
                    data-video-id={video.id}
                    data-account={accountId}
                    data-player={playerId}
                    data-embed="default"
                    data-application-id=""
                    data-analytics="video"
                    data-analytics-video-id={video.id}
                    data-analytics-playlist-id={playlistId}
                    id={video.uniquePlayerId}
                  >
                    <track kind="captions" />
                  </video>
                </div>
              )}
              <VideoPoster
                posterUrl={video.poster}
                isLoading={isOverlayLoading}
                isVisible={showPosterOverlay}
                onActivate={handleActivate}
                instanceLabel={video.uniquePlayerId}
                errorMessage={
                  playbackState === 'error'
                    ? (errorMessage ?? undefined)
                    : undefined
                }
              />
            </div>
          </div>
        ))}
      </div>
      {isInsidePromoVideo && (
        <VideoDescription
          accountId={accountId}
          policyKey={policyKey}
          playlistId={playlistId}
        />
      )}
    </div>
  )
}
